import http from '~/shared/http';
import { jsonRequestHeaders } from '~/csrf';

const PENDING_EPISODE_LIKE_KEY = 'poddtoppen:pending-episode-like';

export function isPersistedEpisodeId(episodeId: unknown): episodeId is number {
    return Number.isInteger(episodeId) && Number(episodeId) > 0;
}

function readSessionStorage(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeSessionStorage(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Ignore storage failures.
    }
}

function removeSessionStorage(key: string): void {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // Ignore storage failures.
    }
}

export async function fetchEpisodeLikeStatus(episodeId: number): Promise<boolean> {
    const { data } = await http.get(`/me/episode-likes/${episodeId}`, {
        headers: jsonRequestHeaders(),
    });

    return Boolean(data?.data?.liked);
}

interface RuntimeEpisodeLikePayload {
    itunes_id: string;
    media: string;
}

interface EpisodeLikeResponse {
    data?: {
        episode?: {
            id?: number;
        };
    };
}

export async function likeEpisode(
    episode: number | RuntimeEpisodeLikePayload,
): Promise<number | null> {
    const payload = typeof episode === 'number' ? { episode_id: episode } : episode;
    const { data } = await http.post<EpisodeLikeResponse>(
        '/me/episode-likes',
        payload,
        { headers: jsonRequestHeaders() },
    );

    return data?.data?.episode?.id ?? null;
}

export async function unlikeEpisode(episodeId: number): Promise<void> {
    await http.delete(`/me/episode-likes/${episodeId}`, {
        headers: jsonRequestHeaders(),
    });
}

export function setPendingEpisodeLike(episodeId: number): void {
    writeSessionStorage(PENDING_EPISODE_LIKE_KEY, String(episodeId));
}

export function getPendingEpisodeLike(): number | null {
    const value = readSessionStorage(PENDING_EPISODE_LIKE_KEY);

    if (!value) {
        return null;
    }

    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function clearPendingEpisodeLike(): void {
    removeSessionStorage(PENDING_EPISODE_LIKE_KEY);
}
